import React from 'react';
import { useReferenceObjectAdmin } from '../../../../hooks/campaign-hooks';
import { CampaignCapability } from '../../../../support/campaigns';
import { ReferenceOption } from './ReferenceSelect';

/**
 * The campaign's capability catalogue as picker options, plus a stub-create for "+ New capability".
 *
 * Both the schema-driven payload editor (`SchemaPayloadFields`, a `capabilityRefs` field) and the
 * question-block editor (`StepQuestionsFields`) offer a capability picker backed by the same DB list
 * and the same blank-keyed create, so the option-mapping and the create payload lived in two places
 * and could drift. This hook is that shared source: it does not touch either editor's form model — it
 * just returns the options and an async create that resolves to the new capability's `key` (or null),
 * exactly the shape `ReferenceSelect`/`ReferenceMultiSelect` expect for `options` and `onCreate`.
 *
 * Capabilities are global (globalization 2026-09-25): "+ New capability" creates a global capability
 * and links it to this campaign in one flow (see `useReferenceObjectAdmin`), and the options are the
 * campaign's linked subset — so a picker only ever offers capabilities this campaign references.
 *
 * `createCapability` returns null when there is no campaign to attach to or the create fails, which the
 * pickers treat as "nothing was created" (no selection change) — the same tolerant behaviour the two
 * editors had inline.
 */
export function useCampaignCapabilityReference(campaignId?: number): {
  capabilityOptions: ReferenceOption[];
  createCapability: () => Promise<string | null>;
} {
  const admin = useReferenceObjectAdmin<CampaignCapability>('capability', campaignId);
  const capabilities = admin.linked;

  const capabilityOptions: ReferenceOption[] = React.useMemo(
    () =>
      capabilities.map((c) => ({
        value: c.key,
        label: c.name || c.key || '(unnamed)',
        hint: c.name && c.key && c.name !== c.key ? c.key : undefined,
      })),
    [capabilities],
  );

  const createCapability = React.useCallback(async (): Promise<string | null> => {
    if (!campaignId) return null;
    const key = `play-${Date.now().toString(36)}`;
    try {
      await admin.createLinked({
        key,
        name: '',
        description: '',
        detail: '',
        proposal: '',
        resource_links: [],
        order: 0,
        hidden: false,
        metadata: {},
      });
      return key;
    } catch {
      return null;
    }
    // admin.createLinked is a fresh closure each render; campaignId is the meaningful dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  return { capabilityOptions, createCapability };
}
